import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Download, Phone } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtGBP = (pence) =>
    `£${((pence || 0) / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) =>
    iso
        ? new Date(iso).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "long",
              year: "numeric",
          })
        : "";

const PublicInvoice = () => {
    const { id } = useParams();
    const [params] = useSearchParams();
    const token = params.get("t");
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/public/invoices/${id}?t=${encodeURIComponent(token || "")}`)
            .then((r) => setData(r.data))
            .catch(() => setErr("Invoice not found or link expired."));
    }, [id, token]);

    if (err) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-neutral-100 p-6">
                <div className="max-w-md text-center border-2 border-red-300 bg-red-50 p-8">
                    <p className="font-bold text-red-800">{err}</p>
                </div>
            </div>
        );
    }
    if (!data) return <p className="p-8 text-neutral-500">Loading…</p>;

    const inv = data.invoice;
    const biz = data.business;
    const pdfUrl = `${API}/public/invoices/${id}/pdf?t=${encodeURIComponent(token || "")}`;

    return (
        <div className="min-h-screen bg-neutral-100 py-10" data-testid="public-invoice-page">
            <div className="max-w-3xl mx-auto bg-white border-2 border-neutral-900 shadow-lg">
                <div className="bg-neutral-900 text-white px-8 py-6">
                    <div className="flex justify-center">
                        <img
                            src="/dragon-heat-logo.png"
                            alt="Dragon Heat Plumbing & Heating"
                            className="h-20 sm:h-24 w-auto"
                            data-testid="public-invoice-logo"
                        />
                    </div>
                    <div className="mt-5 flex items-center justify-between gap-4">
                        <div>
                            <p className="text-xs uppercase tracking-widest text-orange-400 font-black">
                                Invoice
                            </p>
                            <h1 className="text-3xl font-black" data-testid="public-invoice-number">
                                {inv.number}
                            </h1>
                        </div>
                        <a
                            href={pdfUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase text-xs tracking-wide px-4 py-2"
                            data-testid="public-invoice-download"
                        >
                            <Download size={16} /> Download PDF
                        </a>
                    </div>
                </div>

                <div className="px-8 pt-6 grid grid-cols-2 gap-6">
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
                            From
                        </p>
                        <p className="font-bold mt-1">
                            {biz?.business_trading_as || biz?.business_name}
                        </p>
                        <p className="text-sm text-neutral-600">{biz?.business_owner}</p>
                        <p className="text-sm text-neutral-600">{biz?.business_address}</p>
                        <p className="text-sm text-neutral-600">
                            {biz?.business_phone} · {biz?.business_email}
                        </p>
                        {biz?.business_website && (
                            <p className="text-sm text-neutral-600">{biz.business_website}</p>
                        )}
                        <p className="text-xs text-neutral-500 mt-1">
                            {biz?.business_type}
                            {biz?.vat_registered && biz?.vat_number
                                ? ` · VAT ${biz.vat_number}`
                                : ""}
                        </p>
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
                            Bill to
                        </p>
                        <p className="font-bold mt-1" data-testid="public-invoice-customer">
                            {inv.customer_name}
                        </p>
                        <p className="text-sm text-neutral-600 whitespace-pre-line">{inv.address}</p>
                        {inv.customer_email && (
                            <p className="text-sm text-neutral-600">{inv.customer_email}</p>
                        )}
                        <p className="text-xs text-neutral-500 mt-3">
                            Issued <strong>{fmtDate(inv.issued_at)}</strong>
                        </p>
                        <p className="text-xs text-neutral-500">
                            Terms: {biz?.invoice_payment_terms || "Due on receipt"}
                        </p>
                    </div>
                </div>

                <div className="px-8 mt-8">
                    <table className="w-full border-collapse text-sm">
                        <thead>
                            <tr className="bg-neutral-900 text-white text-left">
                                <th className="px-4 py-2 text-xs uppercase tracking-wide">Description</th>
                                <th className="px-4 py-2 text-xs uppercase tracking-wide text-right w-16">Qty</th>
                                <th className="px-4 py-2 text-xs uppercase tracking-wide text-right w-24">Unit</th>
                                <th className="px-4 py-2 text-xs uppercase tracking-wide text-right w-32">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {((inv.line_items && inv.line_items.length > 0)
                                ? inv.line_items
                                : [{
                                    name: inv.description || inv.service_name || "Service",
                                    quantity: 1,
                                    unit_price_pence: inv.subtotal_pence,
                                    total_pence: inv.subtotal_pence,
                                }]
                            ).map((li, idx) => (
                                <tr key={idx} className="border-b border-neutral-200">
                                    <td className="px-4 py-3 align-top">
                                        <p className="font-bold">{li.name}</p>
                                    </td>
                                    <td className="px-4 py-3 text-right align-top">
                                        {(li.quantity ?? 1).toString()}
                                    </td>
                                    <td className="px-4 py-3 text-right align-top">
                                        {fmtGBP(li.unit_price_pence)}
                                    </td>
                                    <td className="px-4 py-3 text-right align-top">
                                        {fmtGBP(li.total_pence)}
                                    </td>
                                </tr>
                            ))}
                            {inv.notes && (
                                <tr className="border-b border-neutral-200">
                                    <td colSpan={4} className="px-4 py-3 text-neutral-500 text-xs whitespace-pre-line">
                                        {inv.notes}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={3} className="px-4 py-2 text-right text-sm text-neutral-600">Subtotal</td>
                                <td className="px-4 py-2 text-right">{fmtGBP(inv.subtotal_pence)}</td>
                            </tr>
                            {inv.vat_pence > 0 && (
                                <tr>
                                    <td colSpan={3} className="px-4 py-2 text-right text-sm text-neutral-600">VAT</td>
                                    <td className="px-4 py-2 text-right">{fmtGBP(inv.vat_pence)}</td>
                                </tr>
                            )}
                            <tr className="border-t-2 border-neutral-900">
                                <td colSpan={3} className="px-4 py-3 text-right font-black uppercase tracking-wide">
                                    Total due
                                </td>
                                <td className="px-4 py-3 text-right text-2xl font-black text-orange-600" data-testid="public-invoice-total">
                                    {fmtGBP(inv.total_pence)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <div className="px-8 mt-8 pb-8">
                    <div className="border-2 border-neutral-900 p-5">
                        <p className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
                            Pay by bank transfer
                        </p>
                        <p className="mt-2">
                            <strong>{biz?.bank_account_name}</strong>
                        </p>
                        <p className="text-sm text-neutral-700">
                            Sort code: <strong>{biz?.bank_sort_code}</strong> · Account:{" "}
                            <strong>{biz?.bank_account_number}</strong>
                        </p>
                        {biz?.bank_name && (
                            <p className="text-sm text-neutral-700">{biz.bank_name}</p>
                        )}
                        <p className="text-sm text-neutral-700 mt-2">
                            Reference: <strong>{inv.number}</strong>
                        </p>
                    </div>

                    <p className="text-xs text-neutral-500 mt-6 flex items-center gap-2">
                        <Phone size={12} /> Any questions? Call {biz?.business_phone} or email {biz?.business_email}.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PublicInvoice;
